import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlass,
  X,
  Clock,
  Tag,
  WifiSlash,
} from '@phosphor-icons/react';
import api, { getTopFromSearchPlaces, getTags } from '../services/api';
import PlaceCard from '../components/PlaceCard';
import telegramService from '../services/telegram';
import { getSearchHistory, saveSearchQuery, clearSearchHistory } from '../utils/searchHistory';
import { pluralize, PLACES } from '../utils/plural';

// Чипы «Часто ищут» — настоящие теги из Strapi, а не список в коде.
// Захардкоженные строки разъезжались бы с базой при первом же
// переименовании тега и показывали бы «Ничего не найдено».
//
// Этот порядок — предпочтительный: перечисленные теги встают первыми
// и именно в таком порядке. Всё, чего в базе не окажется, просто
// пропускается, а недостающие чипы добираются остальными тегами
// по полю order. Список правится в админке Strapi, без разработчика.
const PREFERRED_TAGS = [
  'Бары',
  'Неформальный Петербург',
  'Парки и экотропы',
  'Пространства',
  'Музеи',
];

const POPULAR_TAGS_COUNT = 5;

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

// Места под тегами прячутся, пока данных мало: при двух-трёх кликах
// порядок случаен, и подборка покажет шум вместо популярного
const TOP_FROM_SEARCH_LIMIT = 6;
const TOP_FROM_SEARCH_MIN_CLICKS = 3;

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  // Начальное значение читается один раз: дальше поле — единственный
  // источник правды, в адрес запрос не пишется (см. комментарий ниже)
  const [query, setQuery] = useState(() => searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  // Ошибка сети разведена с пустой выдачей: раньше при сбое results пустел
  // и показывалось «Попробуйте изменить запрос» — человек менял запрос,
  // а проблема была в сети
  const [failed, setFailed] = useState(false);

  const [history, setHistory] = useState(() => getSearchHistory());
  const [topFromSearch, setTopFromSearch] = useState([]);
  const [popularTags, setPopularTags] = useState([]);
  // Показывали ли уже хоть какой-то ответ на текущий сеанс ввода.
  // Нужно, чтобы скелетон поднимался только на первый запрос, а не
  // вклинивался между двумя одинаковыми «Ничего не найдено»
  const [answered, setAnswered] = useState(false);

  const inputRef = useRef(null);
  // Номер запроса: медленный ранний ответ не должен перезаписывать поздний
  const requestIdRef = useRef(0);

  const trimmed = query.trim();
  const isSearching = trimmed.length >= MIN_QUERY_LENGTH;

  // Места, которые чаще открывают из поиска. Грузится один раз, независимо
  // от ввода: блок принадлежит пустому экрану
  useEffect(() => {
    let cancelled = false;
    getTopFromSearchPlaces(TOP_FROM_SEARCH_LIMIT, TOP_FROM_SEARCH_MIN_CLICKS)
      .then((response) => {
        if (!cancelled) setTopFromSearch(response.data || []);
      });
    return () => { cancelled = true; };
  }, []);

  // Теги для чипов. Сначала перечисленные в PREFERRED_TAGS и в их порядке,
  // затем — остальные по order из Strapi, пока не наберётся пять
  useEffect(() => {
    let cancelled = false;

    getTags()
      .then((response) => {
        if (cancelled) return;

        const all = (response.data || []).filter(tag => tag?.name);
        const byName = new Map(all.map(tag => [tag.name.toLowerCase(), tag]));

        const preferred = PREFERRED_TAGS
          .map(name => byName.get(name.toLowerCase()))
          .filter(Boolean);

        const takenIds = new Set(preferred.map(tag => tag.id));
        const rest = all.filter(tag => !takenIds.has(tag.id));

        // Добор остальными тегами — только если PREFERRED_TAGS короче,
        // чем нужно чипов. Ненайденный тег место НЕ освобождает: раньше
        // его молча занимал случайный тег по order — на проде вместо
        // отсутствующих «Музеи» вставала «Индийская». Показать на один
        // чип меньше честнее, чем подсунуть чужой
        const chips = PREFERRED_TAGS.length >= POPULAR_TAGS_COUNT
          ? preferred
          : [...preferred, ...rest];

        setPopularTags(chips.slice(0, POPULAR_TAGS_COUNT));
      })
      // Чипы необязательны: не вышло — показываем экран без них
      .catch(() => {});

    return () => { cancelled = true; };
  }, []);

  const runSearch = useCallback((value) => {
    const requestId = ++requestIdRef.current;

    setLoading(true);
    setFailed(false);

    api.get('/places/search', { params: { query: value, limit: 20 } })
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        setResults(response.data.data || []);
        setLoading(false);
        setAnswered(true);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        // Прошлую выдачу не стираем — она всё ещё осмысленный ответ
        // на прошлый запрос, а поверх неё встанет сообщение о сбое
        setFailed(true);
        setLoading(false);
        setAnswered(true);
      });
  }, []);

  useEffect(() => {
    if (!isSearching) {
      // Отменяем незавершённый запрос: его ответ не должен прилететь
      // на очищенное поле
      requestIdRef.current++;
      setResults([]);
      setLoading(false);
      setFailed(false);
      // Поле опустело — следующий ввод начинается с чистого листа,
      // и скелетон на первый запрос показать снова нужно
      setAnswered(false);
      return;
    }

    const timer = setTimeout(() => runSearch(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed, isSearching, runSearch]);

  // ⚠️ Запрос в адресную строку НЕ пишется. Layout собирает ключ кеша
  // KeepAlive из `pathname + search` (Layout.jsx), поэтому каждая правка
  // `?q=` — это для него другая страница: SearchPage размонтировалась
  // и монтировалась заново на каждую букву. Поле теряло фокус и
  // недопечатанный символ, текст «скакал». Открывать поиск ссылкой
  // с `?q=` по-прежнему можно — значение читается один раз при монтировании.

  // История пишется и здесь тоже. Раньше запрос запоминался только при
  // поиске через шапку, и история появлялась через раз
  useEffect(() => {
    if (!isSearching) return;
    const timer = setTimeout(() => {
      saveSearchQuery(trimmed);
      setHistory(getSearchHistory());
    }, 1200);
    return () => clearTimeout(timer);
  }, [trimmed, isSearching]);

  const handleClear = () => {
    setQuery('');
    inputRef.current?.focus();
  };

  const handleClearHistory = () => {
    telegramService.hapticFeedback('light');
    clearSearchHistory();
    setHistory([]);
  };

  const handleSuggestion = (text) => {
    telegramService.hapticFeedback('light');
    setQuery(text);
    inputRef.current?.focus();
  };

  const showSuggestions = !isSearching;
  const hasResults = results.length > 0;

  return (
    /* Все секции по одной линии — поля экрана px-4 (16px), брендбук 10.1.
       Вертикальный ритм между секциями один: mt-8 (32px), как в Home */
    <div
      className="min-h-screen bg-theme-bg pb-6 max-w-app mx-auto"
      style={{ paddingTop: 'calc(var(--safe-area-top) + var(--tg-header-height))' }}
    >
      {/* Шапка с полем ввода: остаётся на месте при прокрутке выдачи */}
      <div
        className="bg-theme-bg sticky z-sticky"
        style={{ top: 'calc(var(--safe-area-top) + var(--tg-header-height))' }}
      >
        <div className="px-4 py-3">
          <h1 className="text-h2 text-theme-text-primary mb-3">Поиск</h1>

          <div className="relative">
            <MagnifyingGlass
              size={20}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-text-secondary pointer-events-none"
            />
            <input
              ref={inputRef}
              type="text"
              /* Про задачу, а не про механику. Района здесь нет намеренно:
                 поле не заполняется в админке, искать по нему не по чему —
                 подсказка обещала бы то, чего поиск не умеет */
              placeholder="Название или тег"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              /* h-11 (44px) — норма тач-таргета. Радиус md 10px: full
                 остаётся круглым кнопкам и чипам, брендбук 10.2.
                 Обводка — токен focus, а не хардкод focus:ring-blue-500 */
              className="w-full h-11 rounded-md bg-theme-bg-secondary text-theme-text-primary text-body pl-10 pr-10 outline-none border border-transparent transition-colors duration-fast focus:border-[var(--color-focus)]"
            />
            {query && (
              /* onPointerDown, а не onClick: тап по крестику сначала уводит
                 фокус с поля, и click мог не дойти до кнопки — крестик
                 срабатывал через раз. preventDefault держит фокус в поле,
                 чтобы клавиатура не закрывалась после очистки */
              <button
                onPointerDown={(e) => {
                  e.preventDefault();
                  handleClear();
                }}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full transition-opacity duration-fast active:opacity-60"
                aria-label="Очистить поиск"
              >
                <X size={16} className="text-theme-text-secondary" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Пустой экран: история, популярные запросы и места из статистики.
          Раньше здесь была одна лупа с надписью «Введите запрос» */}
      {showSuggestions && (
        <>
          {history.length > 0 && (
            <section className="mt-8 px-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-h2 text-theme-text-primary">Вы искали</h2>
                {/* Красным: это удаление, а не переход. Синий тут читался бы
                    как обычное действие — брендбук 2.1 */}
                <button
                  onClick={handleClearHistory}
                  className="text-caption text-danger transition-opacity duration-fast active:opacity-60"
                >
                  Очистить
                </button>
              </div>
              <div className="flex flex-col">
                {history.map((item) => (
                  <button
                    key={item}
                    onClick={() => handleSuggestion(item)}
                    className="flex items-center gap-3 h-11 text-left transition-colors duration-fast active:bg-theme-card-secondary rounded-md -mx-2 px-2"
                  >
                    <Clock size={20} className="text-theme-text-secondary flex-shrink-0" />
                    <span className="text-body text-theme-text-primary truncate">{item}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          <section className="mt-8 px-4">
            <h2 className="mb-4 text-h2 text-theme-text-primary">Часто ищут</h2>
            {popularTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {popularTags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => handleSuggestion(tag.name)}
                    className="flex items-center gap-2 h-11 px-4 rounded-full bg-theme-card-secondary text-label text-theme-text-primary transition-colors duration-fast active:bg-theme-border"
                  >
                    {/* Один глиф на все чипы: теги приходят из админки,
                        своя иконка у каждого была бы таблицей соответствия,
                        которую пришлось бы вести вручную под каждый новый тег */}
                    <Tag size={16} className="text-theme-text-secondary flex-shrink-0" />
                    {tag.name}
                  </button>
                ))}
              </div>
            )}

            {/* Места идут сразу под тегами, своего заголовка у них нет:
                это продолжение того же блока — с чего начать поиск.
                Пока данных мало, показываются одни теги */}
            {topFromSearch.length > 0 && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                {topFromSearch.map((place) => (
                  <PlaceCard key={place.id} place={place} />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* Выдача */}
      {isSearching && (
        <section className="mt-6 px-4">
          {/* Скелетоны повторяют то, что придёт: сетка в две колонки
              с пропорцией фото 3/4, а не вертикальный список.
              Поднимаются только пока ответа не было ни одного: иначе
              на каждую букву неудачного запроса высокий блок скелетонов
              вклинивался между двумя «Ничего не найдено» — экран прыгал */}
          {loading && !answered && (
            <div className="grid grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex flex-col">
                  <div
                    className="w-full bg-theme-card-secondary rounded-lg animate-pulse"
                    style={{ aspectRatio: '3/4' }}
                  />
                  <div className="h-3 bg-theme-card-secondary rounded-sm w-1/2 mt-3 animate-pulse" />
                  <div className="h-4 bg-theme-card-secondary rounded-sm w-3/4 mt-2 animate-pulse" />
                </div>
              ))}
            </div>
          )}

          {failed && !hasResults && (
            <div className="bg-theme-card-secondary rounded-md p-6 text-center">
              <WifiSlash size={32} weight="light" className="mx-auto mb-3 text-theme-text-hint" />
              <p className="text-body text-theme-text-primary mb-1">Поиск недоступен</p>
              <p className="text-caption text-theme-text-secondary">
                Проверьте соединение и повторите
              </p>
            </div>
          )}

          {/* Держится на месте, пока уточняется следующий запрос: подменять
              его скелетоном на каждую букву незачем — ответ всё равно
              придёт в ту же рамку. Приглушением показываем, что идёт
              пересчёт, — тот же приём, что на кнопке фильтров (Этап 3.3) */}
          {answered && !failed && !hasResults && (
            <div
              className="bg-theme-card-secondary rounded-md p-6 text-center transition-opacity duration-fast"
              style={{ opacity: loading ? 0.75 : 1 }}
            >
              <MagnifyingGlass size={32} weight="light" className="mx-auto mb-3 text-theme-text-hint" />
              <p className="text-body text-theme-text-primary mb-1">Ничего не найдено</p>
              <p className="text-caption text-theme-text-secondary">Попробуйте изменить запрос</p>
            </div>
          )}

          {hasResults && (
            /* Прошлая выдача остаётся на экране, пока грузится следующая, —
               и приглушается, как пустое состояние выше. Схлопывать её
               в скелетон на каждую букву значило бы то же прыганье */
            <div
              className="transition-opacity duration-fast"
              style={{ opacity: loading ? 0.75 : 1 }}
            >
              <p className="mb-4 text-caption text-theme-text-secondary tabular-nums">
                {/* Числительное по полному правилу: «2 места», «11 мест» */}
                Найдено {pluralize(results.length, PLACES)}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {results.map((place) => (
                  /* Подсветка совпадений — как в автокомплите: ищут
                     одинаково, значит и выглядеть должно одинаково */
                  <PlaceCard key={place.id} place={place} highlight={trimmed} />
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default SearchPage;
